import React, { useState, useEffect, useCallback } from 'react';
import { SessionSummary, SessionSource, Agent, ConversationMetrics } from '../types';
import { listAllSessions, listAgents } from '../api/client';
import { MetricsPanel } from './MetricsPanel';
import { MarkdownText } from './MarkdownText';

export function SessionsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [agents, setAgents] = useState<Record<string, Agent>>({});
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [expandedContext, setExpandedContext] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<'all' | 'active' | 'ended'>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | SessionSource>('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [sessionsData, agentsData] = await Promise.all([
        listAllSessions(),
        listAgents(),
      ]);
      setSessions(sessionsData);
      const agentMap: Record<string, Agent> = {};
      agentsData.forEach((a) => { agentMap[a.id] = a; });
      setAgents(agentMap);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = sessions.filter((s) => {
    if (filter !== 'all' && s.status !== filter) return false;
    if (sourceFilter !== 'all' && s.source !== sourceFilter) return false;
    return true;
  });

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        {embedded ? <span /> : <h2 style={styles.heading}>Sessions</h2>}
        <button style={styles.refreshBtn} onClick={load}>Refresh</button>
      </div>

      <div style={styles.filters}>
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>Status:</span>
          {(['all', 'active', 'ended'] as const).map((v) => (
            <button
              key={v}
              style={{ ...styles.filterBtn, ...(filter === v ? styles.filterBtnActive : {}) }}
              onClick={() => setFilter(v)}
            >
              {v}
            </button>
          ))}
        </div>
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>Source:</span>
          {(['all', SessionSource.Direct, SessionSource.Twilio] as const).map((v) => (
            <button
              key={v}
              style={{ ...styles.filterBtn, ...(sourceFilter === v ? styles.filterBtnActive : {}) }}
              onClick={() => setSourceFilter(v)}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={styles.muted}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={styles.muted}>No sessions found</div>
      ) : (
        <div style={styles.list}>
          {filtered.map((s) => {
            const agentName = agents[s.agentId]?.name || s.agentId.slice(0, 8);
            const isOpen = selectedSession === s.id;

            return (
              <div key={s.id} style={styles.card}>
                <div
                  style={styles.cardHeader}
                  onClick={() => setSelectedSession(isOpen ? null : s.id)}
                >
                  <div style={styles.cardLeft}>
                    <span style={styles.agentName}>{agentName}</span>
                    <span style={{
                      ...styles.badge,
                      background: s.source === SessionSource.Twilio ? '#dbeafe' : '#f0fdf4',
                      color: s.source === SessionSource.Twilio ? '#2563eb' : '#16a34a',
                    }}>
                      {s.source}
                    </span>
                    <span style={{
                      ...styles.badge,
                      background: s.status === 'active' ? '#fef3c7' : '#f3f4f6',
                      color: s.status === 'active' ? '#d97706' : '#6b7280',
                    }}>
                      {s.status}
                    </span>
                    {s.context?.callerPhone && (
                      <span style={{ ...styles.badge, background: '#fef3c7', color: '#92400e' }}>
                        {s.context.callerPhone}
                      </span>
                    )}
                    <span style={styles.msgCount}>
                      {(s.messages?.length || 0) / 2 | 0} turns
                    </span>
                  </div>
                  <div style={styles.cardRight}>
                    <span style={styles.date}>{new Date(s.updatedAt).toLocaleString()}</span>
                    <span style={styles.chevron}>{isOpen ? '\u25B2' : '\u25BC'}</span>
                  </div>
                </div>

                {isOpen && (
                  <div style={styles.messages}>
                    <div style={{ padding: '4px 12px 8px', fontSize: 12, color: '#888' }}>
                      <strong>Session ID:</strong>{' '}
                      <code style={{ fontSize: 11, userSelect: 'all' }} title="Paste this id on the Chat connect screen to continue the conversation">{s.id}</code>
                    </div>
                    {(s.context?.callSid || s.context?.callerPhone || s.context?.callbackUrl) && (
                      <div style={{ padding: '8px 12px', background: '#fafafa', borderRadius: 8, marginBottom: 8, fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                          {s.context.callerPhone && <span><strong>Phone:</strong> {s.context.callerPhone}</span>}
                          {s.context.callSid && <span><strong>Call SID:</strong> {s.context.callSid}</span>}
                        </div>
                        {s.context.callbackUrl && (
                          <div style={{ wordBreak: 'break-all' }}>
                            <strong>Callback:</strong> <code style={{ fontSize: 11, color: '#374151' }}>{s.context.callbackUrl}</code>
                          </div>
                        )}
                      </div>
                    )}
                    {s.context?.consumerContext && (() => {
                      const ctxOpen = expandedContext.has(s.id);
                      const toggleCtx = (e: React.MouseEvent) => {
                        e.stopPropagation();
                        setExpandedContext((prev) => {
                          const next = new Set(prev);
                          next.has(s.id) ? next.delete(s.id) : next.add(s.id);
                          return next;
                        });
                      };
                      return (
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderLeft: '3px solid #f59e0b', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
                          <div
                            onClick={toggleCtx}
                            style={{ padding: '6px 12px', background: '#fef3c7', fontSize: 11, fontWeight: 600, color: '#78350f', borderBottom: ctxOpen ? '1px solid #fde68a' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
                          >
                            <span>CONSUMER CONTEXT</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontWeight: 400, color: '#a16207' }}>{s.context!.consumerContext!.length} chars</span>
                              <span style={{ fontSize: 10, color: '#a16207' }}>{ctxOpen ? '▲' : '▼'}</span>
                            </span>
                          </div>
                          {ctxOpen && (
                            <pre style={{ margin: 0, padding: '10px 12px', maxHeight: 320, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12, lineHeight: 1.5, color: '#3f2e0a' }}>{s.context!.consumerContext}</pre>
                          )}
                        </div>
                      );
                    })()}
                    {s.messages?.length > 0 && (() => {
                      const assistantMsgs = s.messages.filter((m: any) => m.role === 'assistant' && m.metrics);
                      const totals = { stt: 0, llm: 0, tts: 0, total: 0, turns: assistantMsgs.length };
                      for (const m of assistantMsgs) {
                        const met = m.metrics!;
                        totals.stt += met.stt?.cost || 0;
                        totals.llm += met.llm?.cost || 0;
                        totals.tts += met.tts?.cost || 0;
                        totals.total += met.totalCost || 0;
                      }
                      if (totals.total === 0) return null;
                      const fmt = (v: number) => v < 0.0001 ? '<$0.0001' : `$${v.toFixed(4)}`;
                      return (
                        <div style={{ padding: '8px 12px', background: '#f0f4ff', borderRadius: 8, marginBottom: 8, fontSize: 12, color: '#444', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                          <span><strong>Session total:</strong> {fmt(totals.total)}</span>
                          {totals.stt > 0 && <span>STT: {fmt(totals.stt)}</span>}
                          {totals.llm > 0 && <span>LLM: {fmt(totals.llm)}</span>}
                          {totals.tts > 0 && <span>TTS: {fmt(totals.tts)}</span>}
                          <span>{totals.turns} turns</span>
                        </div>
                      );
                    })()}
                    {(!s.messages || s.messages.length === 0) ? (
                      <div style={styles.muted}>No messages yet</div>
                    ) : (
                      s.messages.map((m: any, i: number) => (
                        <div key={i} style={styles.message}>
                          <div style={{
                            ...styles.messageRole,
                            color: m.role === 'user' ? '#2563eb' : '#16a34a',
                          }}>
                            {m.role}
                          </div>
                          <div style={styles.messageText}>
                            {m.role === 'assistant' ? <MarkdownText text={m.text} /> : m.text}
                          </div>
                          <div style={styles.messageTime}>
                            {new Date(m.timestamp).toLocaleTimeString()}
                            {m.metrics && !m.metrics.stt && (
                              <span style={{ marginLeft: 8 }}>
                                {m.metrics.totalLatencyMs}ms{m.metrics.totalCost > 0 ? ` | $${m.metrics.totalCost.toFixed(4)}` : ''}
                              </span>
                            )}
                          </div>
                          {m.metrics?.stt && m.metrics?.llm && m.metrics?.tts && (
                            <MetricsPanel metrics={{
                              stt: m.metrics.stt,
                              llm: m.metrics.llm,
                              tts: m.metrics.tts,
                              totalLatencyMs: m.metrics.totalLatencyMs,
                              totalCost: m.metrics.totalCost,
                              wer: m.metrics.wer,
                            } as ConversationMetrics} />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '20px', flex: 1, overflowY: 'auto' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 16,
  },
  heading: { margin: 0, fontSize: 18, color: '#1a1a1a' },
  refreshBtn: {
    padding: '4px 12px', borderRadius: 6, border: '1px solid #ddd',
    background: '#fff', fontSize: 12, cursor: 'pointer', color: '#555',
  },
  filters: {
    display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap',
  },
  filterGroup: { display: 'flex', alignItems: 'center', gap: 4 },
  filterLabel: { fontSize: 12, color: '#888', marginRight: 4 },
  filterBtn: {
    padding: '3px 10px', borderRadius: 6, border: '1px solid #ddd',
    background: '#fff', fontSize: 11, cursor: 'pointer', color: '#666',
  },
  filterBtnActive: {
    background: '#4f46e5', color: '#fff', borderColor: '#4f46e5',
  },
  muted: { color: '#888', fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  card: {
    border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', cursor: 'pointer', background: '#fafafa',
  },
  cardLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  cardRight: { display: 'flex', alignItems: 'center', gap: 8 },
  agentName: { fontSize: 13, fontWeight: 600, color: '#1a1a1a' },
  badge: {
    fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600,
  },
  msgCount: { fontSize: 12, color: '#888' },
  date: { fontSize: 11, color: '#aaa' },
  chevron: { fontSize: 10, color: '#aaa' },
  messages: {
    padding: '12px 16px', background: '#fff',
    borderTop: '1px solid #e5e7eb',
  },
  message: {
    padding: '6px 0', borderBottom: '1px solid #f3f4f6',
  },
  messageRole: {
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const,
    marginBottom: 2,
  },
  messageText: { fontSize: 13, color: '#1a1a1a', lineHeight: 1.4 },
  messageTime: { fontSize: 10, color: '#bbb', marginTop: 2 },
};
